import { clamp } from './statistics'

export type KellyMode = 'full' | 'half' | 'quarter' | 'flat'

export const KELLY_FRACTIONS: Record<KellyMode, number> = {
  full: 1,
  half: 0.5,
  quarter: 0.25,
  flat: 0,
}

// Kelly fraction of bankroll: f* = (p*(d-1) - (1-p)) / (d-1) for decimal odds d.
export function kellyFraction(prob: number, decimalOdds: number): number {
  const p = clamp(prob, 0, 1)
  const b = decimalOdds - 1
  if (b <= 0) return 0
  const f = (p * b - (1 - p)) / b
  return Math.max(0, f)
}

export function fractionalKelly(prob: number, decimalOdds: number, fraction: number): number {
  return kellyFraction(prob, decimalOdds) * clamp(fraction, 0, 1)
}

export interface StakeInputs {
  bankroll: number
  prob: number
  decimalOdds: number
  mode?: KellyMode
  flatPct?: number
  maxStakePct?: number
  maxStakeAbsolute?: number
}

export interface StakeResult {
  fraction: number
  rawFraction: number
  stake: number
  cappedBy: 'kelly' | 'pct-cap' | 'absolute-cap' | 'flat' | 'none'
  mode: KellyMode
  edgePresent: boolean
}

export function recommendedStake(inputs: StakeInputs): StakeResult {
  const mode = inputs.mode ?? 'quarter'
  const maxStakePct = inputs.maxStakePct ?? 0.05
  const fullKelly = kellyFraction(inputs.prob, inputs.decimalOdds)
  const edgePresent = fullKelly > 0

  let rawFraction: number
  if (mode === 'flat') {
    rawFraction = inputs.flatPct ?? 0.01
  } else {
    rawFraction = fullKelly * KELLY_FRACTIONS[mode]
  }

  let fraction = rawFraction
  let cappedBy: StakeResult['cappedBy'] = mode === 'flat' ? 'flat' : 'none'

  if (fraction > maxStakePct) {
    fraction = maxStakePct
    cappedBy = 'pct-cap'
  }

  let stake = fraction * inputs.bankroll
  if (inputs.maxStakeAbsolute !== undefined && stake > inputs.maxStakeAbsolute) {
    stake = inputs.maxStakeAbsolute
    fraction = inputs.bankroll > 0 ? stake / inputs.bankroll : 0
    cappedBy = 'absolute-cap'
  }

  if (mode !== 'flat' && !edgePresent) {
    stake = 0
    fraction = 0
    cappedBy = 'kelly'
  }

  return { fraction, rawFraction, stake, cappedBy, mode, edgePresent }
}

export interface PositionSizeInputs {
  bankroll: number
  entryPrice: number
  stopPrice: number
  riskPctOfBankroll?: number
  maxPositionPctOfBankroll?: number
  allowFractionalShares?: boolean
}

export interface PositionSizeResult {
  shares: number
  positionValue: number
  dollarsAtRisk: number
  riskPerShare: number
  pctOfBankroll: number
  cappedByExposure: boolean
  valid: boolean
}

export function positionSize(inputs: PositionSizeInputs): PositionSizeResult {
  const riskPct = inputs.riskPctOfBankroll ?? 0.01
  const maxPosPct = inputs.maxPositionPctOfBankroll ?? 0.25
  const riskPerShare = Math.abs(inputs.entryPrice - inputs.stopPrice)
  const valid = riskPerShare > 0 && inputs.entryPrice > 0 && inputs.bankroll > 0

  if (!valid) {
    return {
      shares: 0,
      positionValue: 0,
      dollarsAtRisk: 0,
      riskPerShare,
      pctOfBankroll: 0,
      cappedByExposure: false,
      valid: false,
    }
  }

  const dollarsAtRisk = inputs.bankroll * riskPct
  let shares = dollarsAtRisk / riskPerShare

  const maxPositionValue = inputs.bankroll * maxPosPct
  const maxShares = maxPositionValue / inputs.entryPrice
  let cappedByExposure = false
  if (shares > maxShares) {
    shares = maxShares
    cappedByExposure = true
  }

  if (!inputs.allowFractionalShares) shares = Math.floor(shares)

  const positionValue = shares * inputs.entryPrice
  return {
    shares,
    positionValue,
    dollarsAtRisk: shares * riskPerShare,
    riskPerShare,
    pctOfBankroll: inputs.bankroll > 0 ? positionValue / inputs.bankroll : 0,
    cappedByExposure,
    valid: true,
  }
}
